import { useEffect, useMemo, useState } from "react";
import LabelVisibility from "src/api/graphql/__generated__/enums/LabelVisibility";
import IconLock from "src/icons/IconLock";
import { GatheringFilterSortSectionOption } from "../../../types";
import useGatheringFilterSortStore from "../../useGatheringFilterSortStore";
import fetchLabelDataToOption from "../helpers/fetchLabelDataToOption";
import useGatheringLabelsData from "./useGatheringLabelsData";

type LabelOption = GatheringFilterSortSectionOption & {
	visibility: LabelVisibility;
	label: string;
};
const useGatheringLabelsOptions = (gatheringId: string) => {
	const { adminLabelsData, publicLabelsData } = useGatheringLabelsData(gatheringId);
	const [options, setOptions] = useState<LabelOption[]>([]);
	const filterLabels = useGatheringFilterSortStore((state) => state.filterLabels);
	const filterRestrictedLabels = useGatheringFilterSortStore(
		(state) => state.filterRestrictedLabels,
	);
	const toggleFilterLabel = useGatheringFilterSortStore((state) => state.toggleFilterLabel);
	const toggleFilterRestrictedLabel = useGatheringFilterSortStore(
		(state) => state.toggleFilterRestrictedLabel,
	);

	useEffect(() => {
		(async () => {
			const resolvedOptions = await Promise.all([
				...publicLabelsData.map(async (labelData): Promise<LabelOption | null> => {
					const resolved = await fetchLabelDataToOption({
						...labelData,
						visibility: LabelVisibility.Public,
						gatheringId,
					});
					if (!resolved) return null;
					return {
						...resolved,
						visibility: LabelVisibility.Public,
						label: labelData.label,
					};
				}),
				...adminLabelsData.map(async (labelData): Promise<LabelOption | null> => {
					const resolved = await fetchLabelDataToOption({
						...labelData,
						visibility: LabelVisibility.Admin,
						gatheringId,
					});
					if (!resolved) return null;
					delete resolved.hasFallbackImage;
					delete resolved.imageSrc;
					delete resolved.emoji;
					return {
						...resolved,
						visibility: LabelVisibility.Admin,
						label: labelData.label,
						Icon: IconLock,
						imageSrc: undefined,
					};
				}),
			]);
			setOptions(resolvedOptions.filter((option): option is LabelOption => option !== null));
		})();
	}, [adminLabelsData, publicLabelsData, gatheringId]);

	// We need the options to be non-async reactive to clicks
	const parsedOptions = useMemo<GatheringFilterSortSectionOption[]>(() => {
		return options.map(
			(option) =>
				({
					...option,
					isSelected: (option.visibility === LabelVisibility.Public
						? filterLabels
						: filterRestrictedLabels
					).includes(option.label),
					onPress: () => {
						if (option.visibility === LabelVisibility.Public) {
							toggleFilterLabel(option.label);
						}
						if (option.visibility === LabelVisibility.Admin) {
							toggleFilterRestrictedLabel(option.label);
						}
					},
				}) satisfies GatheringFilterSortSectionOption,
		);
	}, [
		options,
		filterLabels,
		filterRestrictedLabels,
		toggleFilterLabel,
		toggleFilterRestrictedLabel,
	]);

	return { labelsOptions: parsedOptions };
};
export default useGatheringLabelsOptions;
